import React, { forwardRef, useImperativeHandle, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { LockBodyScroll } from "@default/lib/use-lock-body-scroll";
import { FocusTrap } from "focus-trap-react";
import { AnimatePresence, motion } from "framer-motion";
import useOutsideClickHandler from "@default/hooks/use-outside-click-handler";
import ErrorBoundary from "@default/js/error-boundary";
import FiltersControlReset from "../filters/filters-control-reset";

const FilterModal = forwardRef(
  ({ children, label, id, active, onExitComplete, className = "" }, ref) => {
    const [open, setOpen] = useState(active);
    const modalContent = useOutsideClickHandler(() => {
      setOpen(false);
    }, open);

    /**
     * Add the open and close function to the ref.current
     * so we can use it from outside.
     * @example <button onClick={() => ref.current?.open()} />
     */
    useImperativeHandle(ref, () => ({
      open: () => setOpen(true),
      close: () => setOpen(false),
    }));

    if (typeof window === "undefined") {
      return;
    }

    return createPortal(
      <AnimatePresence mode="wait" onExitComplete={onExitComplete}>
        {open && (
          <>
            {/* <CloseOnEscape onEscape={() => setOpen(false)} /> */}
            {/* <LockBodyScroll /> */}
            <FocusTrap
              focusTrapOptions={{
                allowOutsideClick: true,
              }}
            >
              <div
                className={classNames("position-wrapper", {
                  [className]: className,
                })}
                onTouchMove={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={label}
                id={id}
              >
                <motion.div
                  className="click-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setOpen(false)}
                />
                <motion.div
                  className="modal-inner"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 50 }}
                  ref={modalContent}
                >
                  <ErrorBoundary>{children}</ErrorBoundary>
                  <ErrorBoundary>
                    <div className="d-flex filter-controls">
                      <FiltersControlReset />
                      <button
                        className="btn btn-primary"
                        onClick={() => setOpen(false)}
                      >
                        <FormattedMessage id="overview.controls.filter_apply.label" />
                      </button>
                    </div>
                  </ErrorBoundary>
                </motion.div>
              </div>
            </FocusTrap>
          </>
        )}
      </AnimatePresence>,
      document.getElementById("modal-wrapper")
    );
  }
);

FilterModal.displayName = "FilterModal";

FilterModal.propTypes = {
  label: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export default FilterModal;
